import { AggregateBase } from "csi/action/AggregateBase";
import Controller from "csi/action/Controller";
import FormData from "csi/action/FormData";
import ExtendedPath from "csi/core/ExtendedPath";
import PathHints from "csi/core/PathHints";
import def from "csi/def";
import { Action } from "csi/def/Action";
import Heading from "csi/def/Heading";
import { PreferencePath } from "csi/def/PreferencePath";
import { ITypeImage, Type } from "csi/def/Type";
import { View } from "csi/def/View";
import { create } from "csi/def/ViewFactory";
import viewStoreBuilder from "csi/def/ViewStoreBuilder";
import { FormView } from "csi/form/View";
import csi from "csi/main";
import { getCustomViewAvailablePaths } from "csi/middleware/customView/customViewService";
import { PropertyView } from "csi/property/View";
import ReflectionClass from "csi/reflection/ReflectionClass";
import { getClass, getClassAttribute, getClasses, getDomainClass, getDomainClasses, getTypeAttributeDestination, hasAggregateStoreForAttribute } from "csi/reflection/reflection";
import reflectionHints from "csi/reflectionHints";
import { getFormViewForType } from "csi/service/viewService";
import { DEFAULT_FORM_ID, VIEW_TYPE_TABS } from "csi/state/viewUtils";
import TabsView from "csi/tabs/View";
import { addToGroupedObject } from "csi/utility/objectUtility";
import { pathsAreSame } from "csi/utility/pathUtility";
import csiView from "csi/view";
import declare from "dojo/_base/declare";
import _, { each, sortBy } from "lodash";
import { ATTR_TYPE, C8AttributeId, C8NodeType } from "src/c8-platform/type/node";

type ActionFilter = (a: Action, targetClass: C8NodeType) => boolean;
type HeadingFilter = (rc: ReflectionClass, h: Heading) => boolean;
type ViewHeadingFilter = (h: Heading, defView: View, rcView: ReflectionClass) => void;

const x = (v: any): string => !!v ? "X" : "";

const isAggregateReverseWithAggregateClass = (action: Action): boolean => {
	if ((action.m_tag === "Aggregate" || action.m_tag === "AggregateFrom")) {
		const aggAction = action as AggregateBase;
		return aggAction.bReverse && !!aggAction.aggregateClass;
	}

	return false;
};

const isAggregateActionWithoutViewRef = ({ m_tag, m_viewRef }: Action): boolean => {
	return !m_viewRef && (m_tag === "Aggregate" || m_tag === "AggregateFrom");
};

const isHeadingDependentOnAggregateStore = (rc: ReflectionClass, h: Heading): boolean => !!h.m_dependency && hasAggregateStoreForAttribute(rc.type, h.m_dependency);

const isHeadingWithAggregateInline = (rc: ReflectionClass, h: Heading): boolean => h.m_action?.m_tag === "AggregateInline";

const isHeadingWithName = (rc: ReflectionClass, h: Heading): boolean => !!h.m_name;

const isHeadingWithRelationshipPriorityStore = (rc: ReflectionClass, h: Heading): boolean => h.m_relationship && h.m_action?.m_tag === "AggregateInline";

const isHeadingWithDefaultActionViewRef = (rc: ReflectionClass, h: Heading): boolean => h.m_action && h.m_action.m_viewRef && h.m_action.m_tag !== "AggregateInline";

const isStringSelectionHeadingWithRendererClass = (rc: ReflectionClass, h: Heading): boolean => {
	const rca = rc.getAttribute(h.m_id, true);
	return h.classRenderer && rca?.valueType === "string" && rca?.format === "Selection";
};

function logHeading(rc: ReflectionClass, h: Heading): void {
	console.log(`Heading -> Type: ${rc.type}  Id: ${h.m_id}`, h);
}

/**
 * Return first properties view found for type
 */
function getPropertiesView(rc: ReflectionClass, defType: Type): PropertyView | undefined {
	const viewType = "Property";
	let defView = def.getClassViewDef(rc, "Properties") as View;
	
	if (defView?.m_typeView !== viewType) {
		defType?.traverseViewTags((xn) => {
			if (defType.getViewTagType(xn) === viewType) {
				defView = create(xn, viewType, defType.getType());
				return true;
			}
		});
	}

	return defView as PropertyView;
}

/**
 * Compare property image definition vs node type image definition; ignore index as it would always be "" if it exists
 */
function hasImageConflict(imageDef: ITypeImage, imageHeading: Heading): boolean {
	if (!imageDef || !imageHeading) {
		return false;
	}

	const conflict = (imageHeading.m_id !== imageDef.m_id) ||
		!pathsAreSame(imageHeading.m_path, imageDef.m_path);
	
	return conflict;
}

/**
 * Report which types have layout view, default card and properties view
 */
function checkPropertiesVsDefaultHeadings(): void {
	const title = "Default Card Report";
	const { document } = window.open("", title);
	const noHeadings: string[] = [];

	document.write(`<HTML><HEAD><TITLE>${title}</TITLE></HEAD>`);
	document.write("<BODY><pre>");
	document.write("Class,Default Card,Property View<br/>");

	sortBy(getDomainClasses(), "type").forEach((dc) => {
		const { primary, type } = dc;
		const typeDef = def.typeDefinition(type);
		const defaultCard = typeDef?.getCard(def.getDefaultCardId(type));
		const properties = getPropertiesView(primary, typeDef);

		if (properties || defaultCard) {
			properties?.resolve(primary);

			const keys1 = defaultCard?.m_headings.map((h) => h.getKey());
			const keys2 = properties?.m_headings.map((h) => h.getKey());
			const n = Math.max(keys1?.length || 0, keys2?.length || 0);

			for (let i = 0; i < n; ++i) {
				document.write(`${type},${keys1?.[i] || ""},${keys2?.[i] || ""}<br/>`);
			}
		} else if (typeDef?.layoutView) {
			document.write(`${type},,<br/>`);
		}
	});

	document.write("</pre></BODY></HTML>");
	document.close();
}

/**
 * Report types with layout view and their properties view id
 */
interface IJsonReportLayout {
	propertyViewId?: string;
	revision?: boolean;
}

function jsonReportOfTypesWithViewsAndProperties(): void {
	const title = "Layout And Properties";
	const { document } = window.open("", title);
	const json = {};

	document.write(`<HTML><HEAD><TITLE>${title}</TITLE></HEAD>`);
	document.write("<BODY><pre>");

	getClasses().forEach((rc) => {
		const { revisionType, type } = rc;

		if (!rc.isRevision() && !rc.abstract) {
			const typeDef = def.typeDefinition(type);
			const revisionTypeDef = revisionType ? def.typeDefinition(revisionType) : null;
			const layoutDef = revisionTypeDef || typeDef;
			const properties = getPropertiesView(rc.getRevisionClass() || rc, layoutDef);

			if (layoutDef?.layoutView) {
				const obj: IJsonReportLayout = json[type] = {};

				if (properties?.m_id) {
					obj.propertyViewId = properties.m_id;
				}

				if (revisionTypeDef) {
					obj.revision = true;
				}
			}
		}
	});

	document.write(csi.objectToString(json, 0, "\n"));
	document.write("</pre></BODY></HTML>");
	document.close();
}

/**
 * Report which types have layout view, default card and properties view
 */
function checkTypesWithViewsAndProperties(): void {
	const title = "Navigation by Type Report";
	const { document } = window.open("", title);
	document.write(`<HTML><HEAD><TITLE>${title}</TITLE></HEAD>`);
	document.write("<BODY><pre>");
	document.write("Class,BO,Layout,Default Card,Properties,Image Id,Image Path,Image Conflict<br/>");

	getClasses().forEach((rc) => {
		const { alias, revisionType, type } = rc;

		if (!rc.isRevision() && !rc.abstract) {
			const typeDef = def.typeDefinition(type);
			const revisionTypeDef = revisionType ? def.typeDefinition(revisionType) : null;
			const layoutDef = revisionTypeDef || typeDef;
			const properties = getPropertiesView(rc.getRevisionClass() || rc, layoutDef);
			const defaultCard = typeDef?.getCard(def.getDefaultCardId(type));
			const imageDef = properties?.getImage();
			const imageConflict = hasImageConflict(typeDef?.image, imageDef);

			document.write(`${type},${x(alias)},${x(layoutDef?.layoutView)},${x(defaultCard)},${properties?.m_id || ""},${imageDef?.m_id || ""},${imageDef?.m_path || ""},${x(imageConflict)}<br/>`);
		}
	});

	document.write("</pre></BODY></HTML>");
	document.close();
}

/**
 * Traverse headings defined for all types
 */
function checkAllTypeHeadings(filter: HeadingFilter): void {
	getClasses().forEach((rc) => {
		const typeDef = def.typeDefinition(rc.type);
		const { headingXmlNodes } = typeDef;

		if (headingXmlNodes) {
			for (const value of Object.values(headingXmlNodes)) {
				const h = new Heading(value);

				if (filter(rc, h)) {
					logHeading(rc, h);
				}
			}
		}
	});
}

/**
 * Traverse all headings in all views
 */
function checkAllViewHeadings(callback: ViewHeadingFilter): void {
	getClasses().forEach((rc) => {
		const defType = def.typeDefinition(rc.type);

		defType?.traverseViewTags((viewTag) => {
			const defView = defType._createViewDef(viewTag);
			const rcView = defView.getPageClass(rc);
			if (rcView) {
				defView.resolve(rc);
				defView.getHeadings()?.forEach((h) => callback(h, defView, rcView));
			}
		});
	});
}

/**
 * Check that all headings using Id are actually in the model
 */
function checkAllViewHeadingsAreInModel(): void {
	checkAllViewHeadings((h, defView, rcView) => {
		if (h.m_id && (h.m_id !== ATTR_TYPE) && (defView.m_typeView !== "Form")) {
			const rel = defView.getPageRelationship();
			const pathHints = rel ? rel.pathHintsContext : defView.pathHintsContext;
			const ep = ExtendedPath.create(rcView, h.m_path, pathHints);
			const rca = getClassAttribute(ep?.endClass, h.m_id);
			if (!rca) {
				console.log(`Not an attribute in view ${defView.toString()}: `, h.m_id, h.m_path);
			}
		}
	});
}

/**
 * Log all views where headings in the view reference paths that are not PreferencePaths
 */
function checkPreferencePaths(): void {
	const checkPaths = (type: C8NodeType, pathHints: PathHints, paths: PreferencePath[]): void => {
		const sourcePath = new ExtendedPath(type, pathHints);

		paths?.forEach((pp) => {
			try {
				const p = sourcePath.plus(pp.m_path);
				if (!p.isSingle) {
					console.error("PLURAL: ", sourcePath.startType, pp.m_path);
				}
			} catch (ex) {
				console.error("ERROR: ", sourcePath.startType, pp.m_path);
			}
		});
	};

	getClasses().forEach((rc) => {
		const { type } = rc;
		const defType = def.typeDefinition(type);

		checkPaths(type, null, defType?.m_prefPaths);

		defType?.traverseViewTags((viewTag) => {
			const defView = defType._createViewDef(viewTag);
			const { m_id, m_idType } = defView;
			
			defView.resolve(rc);
			
			const rcView = defView.getPageClass(rc);
			if (!rcView) {
				console.log(`Could not process view ${m_id} on type ${m_idType}`);
				return;
			}

			checkPaths(type, defView.pathHintsContext, defView.prefPaths);

			const rel = defView.getPageRelationship();
			if (rel) {
				checkPaths(rcView.type, rel.pathHintsContext, rel.m_prefPaths);
			}
		});
	});
}

/**
 * Log all views where headings in the view reference paths that are not PreferencePaths
 */
function checkViewHeadingPathsVsPreferencePaths(): void {
	const typeMap = new Map<C8NodeType, Set<string>>();

	getClasses().forEach((rc) => {
		const { type } = rc;
		const defType = def.typeDefinition(type);

		defType?.traverseViewTags((viewTag) => {
			const defView = defType._createViewDef(viewTag);
			const { m_id, m_idType } = defView;

			defView.resolve(rc);

			const allHeadings = _.flatten([defView.getHeadings() || [], defView.getImages() || []]);
			const headingPaths = def.getHeadingPaths(allHeadings);
			const rel = defView.getPageRelationship();
			const rcView = defView.getPageClass(rc);

			if (!rcView) {
				console.log(`Could not process view ${m_id} on type ${m_idType}`);
				return;
			}

			const pathHints = (rel ? rel.pathHintsContext : defView.pathHintsContext) || new PathHints();
			const sourcePath = ExtendedPath.create(rcView, null, pathHints);
			const prefPaths = rel ? rel.m_prefPaths : defView.prefPaths;
			const extraPaths: PreferencePath[] = [];
			const missingPaths = typeMap[rcView.type] || new Set<string>();

			prefPaths?.forEach((pp) => {
				extraPaths.push(pp);
			});

			const availableExtendedPaths = getCustomViewAvailablePaths(sourcePath, extraPaths);
			const availablePaths = new Set<string>();

			availableExtendedPaths.forEach((ep) => {
				availablePaths.add(ep.toString());
			});

			headingPaths.forEach((path) => {
				try {
					const ep = sourcePath.plus(path, true).resolveToDomain();

					if (ep.isSingle &&
						!ep.inSameDomain &&
						!pathHints.isPathHidden(ep) &&
						!availablePaths.has(ep.toString())
					) {
						missingPaths.add(ep.toString());
					}
				} catch (ex) {
					console.log("Error!", m_id, m_idType, sourcePath.toString(), path);
				}
			});

			if (missingPaths.size) {
				typeMap[rcView.type] = missingPaths;
			}
		});
	});

	console.log("Paths that are not preference paths for type:", typeMap);
}

/**
 * Log all views that omit same path as a Heading uses
 */
function checkViewsThatOmitSamePathAsHeading(): void {
	const views: [string, string, string[]][] = [];
	const pathSet = new Set<string>();

	getClasses().forEach((rc) => {
		const { type } = rc;
		const defType = def.typeDefinition(type);

		defType?.traverseViewTags((viewTag) => {
			const defView = defType._createViewDef(viewTag);
			const { m_id, m_idType } = defView;

			defView.resolve(rc);

			const allHeadings = _.flatten([defView.getHeadings() || [], defView.getImages() || []]);
			const headingPaths = def.getHeadingPaths(allHeadings);
			const rel = defView.getPageRelationship();
			const rcView = defView.getPageClass(rc);
			const pathHintsContext = rel ? rel.pathHintsContext : defView.pathHintsContext;
			const paths = [];

			headingPaths.forEach((path) => {
				const ep = ExtendedPath.create(rcView, path, pathHintsContext);
				if (ep?.isOmitted) {
					paths.push(ep.toString());
					pathSet.add(ep.toString());
				}
			});

			// if any headingPaths are omitted
			if (paths.length) {
				views.push([m_idType, m_id, paths]);
			}
		});
	});

	console.log("Views with omit path same as Heading path", views);
	console.log("Omitted paths", [...pathSet]);
}

/**
 * Check reflectionHints target paths to see if they can be discovered from the domain model (Navigation) rather than specified in client;
 * the server path traversal needs to be in sync with the client (or vice-versa)
 * NOTE: to test, comment out code in ReflectionAttribute#getDestinationClasses to return hint class
 */
function checkReflectionHintsAgainstModel(): void {
	const hints = reflectionHints.getAllHints();
	const diff: [C8NodeType, C8AttributeId, C8NodeType, C8NodeType[]][] = [];
	const abs: [C8NodeType, C8AttributeId, C8NodeType, C8NodeType[]][] = [];
	const same: [C8NodeType, C8AttributeId, C8NodeType][] = [];

	for (const type in hints) {
		const { Child } = hints[type];
		const rc = getClass(type);
		if (Child) {
			for (const id in Child) {
				const targetType = Child[id];
				const dest = getTypeAttributeDestination(type, id)?.type || "";

				if (dest !== targetType) {
					const rca = getClassAttribute(rc, id);
					const dests = rca?.getDestinationClasses(rc)?.map((rc) => rc.type);
					const rcTarget = getClass(targetType);

					if (rcTarget?.abstract) {
						abs.push([type, id, targetType, dests]);
					} else {
						diff.push([type, id, targetType, dests]);
					}
				} else {
					same.push([type, id, dest]);
				}
			}
		}
	}

	console.log("Abstract: ", abs);
	console.log("Diff: ", diff);
	console.log("Same: ", same);
}

/**
 * Log all types that have export views with more than a simple Node Name heading
 */
function checkExportViewsWithNonDefaultHeadings(): void {
	const types: C8NodeType[] = [];

	getClasses().forEach((rc) => {
		const { type } = rc;
		const defType = def.typeDefinition(type);
		const exportView = defType?.getViewDef("__Export__");
		const headings = exportView?.m_headings;

		if (headings && ((headings.length > 1) || !headings[0].isName || headings[0].m_path)) {
			types.push(type);
		}
	});

	console.log("Node types with custom export view", types);
}

/**
 * Output node types whose form is used in a wizard
 */
function checkFormTypesUsedInWizards(): Set<C8NodeType> {
	const types = new Set<C8NodeType>();

	getClasses().forEach((rc) => {
		const { type } = rc;
		const defType = def.typeDefinition(type);

		defType?.traverseViewTags((viewTag) => {
			const typeView = defType.getViewTagType(viewTag);
			if (typeView === "Wizard") {
				const tabsView = defType._createViewDef(viewTag) as TabsView;

				tabsView.tabs.forEach((tab) => {
					if (tab.m_idView === DEFAULT_FORM_ID) {
						types.add(tab.target || type);
					}
				});
			}
		});
	});

	return types;
}

/**
 * Return any types where New action uses form and has custom Request or Controller
 */
function checkFormTypesWithSaveLogic(): Set<C8NodeType> {
	const types = new Set<C8NodeType>();

	checkRelationshipActions((a, targetType) => {
		if (a.m_request || a.classController) {
			types.add(targetType);
		}

		return false;
	}, true);

	return types;
}

/**
 * Log all composite Html views a toolbar (that have custom views) - do only DataSheet views have custom views?
 */
function checkHtmlViewsWithToolbar(): void {
	const views: [string, string][] = [];

	getClasses().forEach((rc) => {
		const { type } = rc;
		const defType = def.typeDefinition(type);

		defType?.traverseViewTags((viewTag) => {
			const defView = defType._createViewDef(viewTag);
			const { m_id, m_idType, m_typeView } = defView;

			if (defView.defToolbar && (m_typeView === "Html")) {
				views.push([m_idType, m_id]);
			}
		});
	});

	console.log("Html views with Toolbar", views);
}

/**
 * Log all views with more than one relationship
 */
function checkViewsWithTwoRelationships(): void {
	const views: [string, string][] = [];

	getClasses().forEach((rc) => {
		const { type } = rc;
		const defType = def.typeDefinition(type);

		defType?.traverseViewTags((viewTag) => {
			const defView = defType._createViewDef(viewTag);
			const { m_id, m_idType } = defView;

			if (defView.getRelationships?.()?.length > 1) {
				views.push([m_idType, m_id]);
			}
		});
	});

	console.log("Multiple Relationships", views);
}

/**
 * Search all views for omitted paths and group by path hints identifier;
 * also report any that are invalid, backtrack (unnecessary), and those that can't be analyzed by this tool for some reason
 */
function checkRelationshipOmitPaths(): void {
	const backtracks: [string, string, string][] = [];
	const invalid: [string, string, string][] = [];
	const targets: [string, string][] = [];

	const processPaths = (
		rc: ReflectionClass,
		prefPaths: PreferencePath[],
		pathHintsContext: PathHints,
		viewId: string,
		viewOwnerType: C8NodeType,
	): void => {
		prefPaths?.forEach((pp) => {
			const ep = ExtendedPath.create(rc, pp.m_path, pathHintsContext);
			if (ep) {
				if (ep.backtracks()) {
					backtracks.push([viewOwnerType, viewId, pp.m_path]);
				}
			} else {
				invalid.push([viewOwnerType, viewId, pp.m_path]);
			}
		});
	};

	getClasses().forEach((rc) => {
		const { type } = rc;
		const defType = def.typeDefinition(type);

		defType?.traverseViewTags((viewTag) => {
			const defView = defType._createViewDef(viewTag);
			const { m_id, m_idType, pathHintsContext, prefPaths } = defView;

			defView.resolve(rc);

			processPaths(rc, prefPaths, pathHintsContext, m_id, m_idType);

			defView.getRelationships()?.forEach((rel) => {
				const { pathHintsContext, m_prefPaths, m_rcTarget } = rel;

				if (m_rcTarget) {
					processPaths(m_rcTarget, m_prefPaths, pathHintsContext, m_id, m_idType);
				} else if (m_prefPaths) {
					targets.push([m_idType, m_id]);
				}
			});
		});
	});

	console.log("Backtrack Omits", backtracks);
	console.log("Invalid Omits", invalid);
	console.log("No Target Paths", targets);
}

function checkRelationshipActions(filterFunc: ActionFilter, newOnly?: boolean): void {
	getClasses().forEach((rc) => {
		const { type } = rc;
		const defType = def.typeDefinition(type);

		defType?.traverseViewTags((viewTag) => {
			const defView = defType._createViewDef(viewTag);
			const logAction = (a): void => {
				console.log(`Type: ${type}  View: ${defView.getId()}  Action: ${a.getId()}`);
			};

			if (!newOnly) {
				defView.defToolbar?.getAllActions().filter((a) => filterFunc(a, type))
					.forEach(logAction);
			}

			defView.getRelationships()?.forEach((rel) => {
				const targetType = rel.getTargetType();
				const actions = newOnly ? rel.m_news : rel.getActions();

				actions?.filter((a) => filterFunc(a, targetType))
					.forEach(logAction);
			});
		});
	});
}

function formClassification(fd: FormData): void {
	const title = "Forms";
	const { document } = window.open("", title);
	document.write(`<HTML><HEAD><TITLE>${title}</TITLE></HEAD>`);
	document.write("<BODY><pre>");
	document.write("Class,BO,Custom Field,OnChange Logic,Controller,In Wizard,Save Logic,Error<br/>");
	
	const saveLogicTypes = checkFormTypesWithSaveLogic();
	const wizardFormTypes = checkFormTypesUsedInWizards();

	_.sortBy(getClasses(), "type").forEach((rc) => {
		const { alias, type } = rc;
		const formDef = getFormViewForType(type);
		
		// omit revision classes since they will have same form as master
		if (formDef && !rc.isRevision()) {
			formDef.setFormData(fd);

			const formView = formDef as FormView;
			let error = false;

			try {
				csiView.newView(formDef, null, { bPopup: true }, true);
			} catch (ex) {
				error = true;
				console.warn("Controller error:", type);
			}

			const { classController } = formDef;
			const custom = formView.m_fields.some((f) => !f.m_id);
			const onChange = formView.m_fields.some((f) => !!f.m_onChange);
			const inWizard = wizardFormTypes.has(type);
			const hasSaveLogic = inWizard || saveLogicTypes.has(type);

			document.write(`${type},${x(alias)},${x(custom)},${x(onChange)},${x(classController)},${x(inWizard)},${x(hasSaveLogic)},${x(error)}<br/>`);
		}	
	});	

	document.write("</pre></BODY></HTML>");
	document.close();
}

// keep for now - running list of possible 7.10 form replacements
const simpleForms = [
	"AcceptanceLimit",
	"AllergenItem",
	"ApparelBOMSubtype",
	"ApparelDimension",
	"ArtworkSubtype",
	"Avatar",
	"BOMSectionDefinition",
	"CamundaTaskAction",
	"CareAndCompositionSubtype",
	"Category1",
	"Category2",
	"Collection",
	"ColorMatchingType",
	"ColorPaletteSubtype",
	"ColorSpecification",
	"CommentCategorySetup",
	"CompanyInfo",
	"CompetitiveFood",
	"CompetitiveStyle",
	"CompositionPlacement",
	"ConfigurableAttributeGroup",
	"ConfigurableValidationRule",
	"ConfigurableValidationRuleGroup",
	"ContractualDocumentSubtype",
	"Customer",
	"CustomerPurchaseOrder",
	"DocumentSubtype",
	"EnumValue",
	"SustainabilityProfile",
	"SustainabilityProfileSubtype",
	"ExtractStore",
	"Factory",
	"Favorite",
	"FileSystemFolder",
	"FinalInspectionPickPlan",
	"FinalInspectionSectionSubtype",
	"FoodAllergen",
	"FoodClaim",
	"FoodIngredient",
	"FoodLabelDataSheet",
	"FoodLabelDataSheetSubtype",
	"FoodNutrient",
	"FoodSubtype",
	"FormulatedMaterialSubtype",
	"Group",
	"HierarchySecurityGroup",
	"Holiday",
	"HolidayCalendar",
	"Increment",
	"InspectionTestSpec",
	"Issue",
	"JasperServerInfo",
	"LDAPServerConfiguration",
	"Language",
	"LookupItem",
	"LookupItemSubtype",
	"ManufacturerCode",
	"MarketingCollection",
	"MaterialBOMSubtype",
	"MaterialType",
	"MerchCollectionSubtype",
	"MerchFolderSubtype",
	"MerchOptionSubtype",
	"MerchPlanSubtype",
	"MerchProductSubtype",
	"MerchProductVersionCopyTemplate",
	"MerchSecondaryPlanSubtype",
	"MicroServiceConnector",
	"NotificationTemplate",
	"NutrientItem",
	"PackList",
	"Placement",
	"ProductGroup",
	"ProductHierarchyFolderSubtype",
	"ProductHierarchySubtype",
	"ProductPresentationSet",
	"ProductSalesRegionSpec",
	"ProjectLineItem",
	"ProjectSubtype",
	"QuestionSection",
	"RecipeDataSheetSubtype",
	"Role",
	"SalesDivision",
	"SalesMarket",
	"SalesOrderGroup",
	"SampleStorage",
	"SamplingPlan",
	"SamplingPlanItem",
	"Season",
	"ShippingRate",
	"SizeChartSubtype",
	"SizeSpec",
	"SpecificationDataSheetItem",
	"SpecificationDataSheetSubtype",
	"SpecificationSection",
	"StructItemNameCode",
	"StyleType",
	"SubQuestion",
	"SubRouting",
	"Supplier",
	"SupplierRequestSubtype",
	"SustainabilityCompositionType",
	"TestSpec",
	"TestSpecSubtype",
	"UOMType",
	"UnitOfMeasure",
	"Wbs",
	"Workflow",
	"_CS_PreferenceView",
];

function formAttributesNeedingAggregateStore(domainTypes?: string[]): void {
	const obj: Record<C8NodeType, Heading[]> = {};
	let allClasses: ReflectionClass[] = [];
	
	if (domainTypes) {
		domainTypes.forEach((type) => {
			const dc = getDomainClass(type);
			const { primary, secondaries } = dc;
			const classes: ReflectionClass[] = [primary, ...Object.values(secondaries)];
			const revisionClass = primary.getRevisionClass();
			
			if (revisionClass) {
				classes.push(revisionClass);
			}
		
			classes.forEach((classInDomain) => {
				classInDomain.traverse(true, false, (rc) => {
					allClasses.push(rc);
				});
			});
		});
	} else {
		allClasses = getClasses();
	}

	allClasses.forEach((rc) => {
		const { type } = rc;
		const typeDef = def.typeDefinition(type);
					
		if (typeDef?.headingXmlNodes) {
			for (const value of Object.values(typeDef.headingXmlNodes)) {
				const h = new Heading(value);
	
				if (isHeadingWithAggregateInline(rc, h)) {
					addToGroupedObject(obj, type, h);
				}
			}
		}
	});

	const x = (v: any): string => !!v ? "X" : "";
	const title = "Attributes needing aggregate store for forms";
	const { document } = window.open("", title);
	document.write(`<HTML><HEAD><TITLE>${title}</TITLE></HEAD>`);
	document.write("<BODY><pre>");
	document.write("Class,Attribute,View,Relationship,Renderer<br/>");

	each(obj, (headings, type) => {
		const set = new Set<string>();
		
		for (const h of headings) {
			const id = h.m_id;
			const rel = x(h.m_relationship);
			const ren = x(h.classRenderer);
			const view = x(h.m_action?.m_viewRef);

			if (!set.has(id)) {
				document.write(`${type},${id},${view},${rel},${ren}<br/>`);
				set.add(id);
			}
		}
	});

	document.write("</pre></BODY></HTML>");
	document.close();
}

async function checkLayoutView(portalViewId: string): Promise<void> {
	const typeStore = def.getPortalTypeStore(portalViewId);

	typeStore.query({}).forEach((typeItem) => {
		const type = typeItem.id;
		const viewStore = viewStoreBuilder.getStore(type, portalViewId);
		const hash = {};
		let first = true;

		viewStore.query({}).forEach((viewItem) => {
			const { parent, viewId, viewType } = viewItem;
			const parentViewItem = viewStore.get(parent);

			if ((viewType === VIEW_TYPE_TABS) || (parentViewItem?.viewType === VIEW_TYPE_TABS)) {
				if (hash[viewId]) {
					if (first) {
						console.log("layout check: ", type);
						first = false;
					}

					console.log("duplicate: ", viewId, viewItem);
					console.log("original: ", viewId, viewItem);
				} else {
					hash[viewId] = viewItem;
				}
			}
		});
	});
}

function findViewsOfType(targetType: string): void {
	getClasses().forEach((rc) => {
		const { type } = rc;
		const defType = def.typeDefinition(type);

		defType?.traverseViewTags((viewTag) => {
			const defView = defType._createViewDef(viewTag);
			const rcTarget = defView.getPageClass(rc);

			if (targetType === rcTarget?.type) {
				console.log(`Target Class: ${targetType}  Type: ${type}  ViewId: ${defView.m_id}`);
			}
		});
	});
}

function findCardsOfType(cardType: string, cardId: string): void {
	getClasses().forEach((rc) => {
		const { type } = rc;
		const defType = def.typeDefinition(type);
		const isMatch = ({ m_id, m_idType }): boolean => (m_idType === cardType) && (m_id === cardId);

		defType?.traverseViewTags((viewTag) => {
			const defView = defType._createViewDef(viewTag);

			defView.populateHeadingsFromCards(rc, defView.pathHintsContext);

			if (defView.usedCards?.some(isMatch)) {
				console.log(`Type: ${type}  ViewId: ${defView.m_id}`);
			}
		});
	});
}

window["findCardsOfType"] = findCardsOfType;
window["findViewsOfType"] = findViewsOfType;

export = declare(Controller, {
	async process() {
		// checkPreferencePaths();
		// checkPropertiesVsDefaultHeadings();
		// jsonReportOfTypesWithViewsAndProperties();
		// checkTypesWithViewsAndProperties();
		// checkAllViewHeadingsAreInModel();
		// checkExportViewsWithNonDefaultHeadings();
		// checkHtmlViewsWithToolbar();
		// checkReflectionHintsAgainstModel();
		// checkViewsThatOmitSamePathAsHeading();
		// checkViewHeadingPathsVsPreferencePaths();
		// checkViewsWithTwoRelationships();
		// checkRelationshipOmitPaths();
		// checkAllTypeHeadings(isHeadingDependentOnAggregateStore);
		// checkAllTypeHeadings(isHeadingWithDefaultActionViewRef);
		// checkAllTypeHeadings(isHeadingWithName);
		// checkAllTypeHeadings(isHeadingWithRelationshipPriorityStore);
		// checkAllTypeHeadings(isStringSelectionHeadingWithRendererClass);
		// checkRelationshipActions(isAggregateReverseWithAggregateClass);
		// formClassification(this.getFormData());
		// formAttributesNeedingAggregateStore(simpleForms);
		await checkLayoutView("__Host__");
		await checkLayoutView("__Supplier__");
		this.postProcess(true);
	},
});
